/**
 * 确认对话框 — 替代 window.confirm()，统一样式 + 不阻塞 UI
 *
 * 用 Zustand store 驱动，任何组件都能调用：
 *   const confirm = useConfirmStore((s) => s.confirm)
 *   const ok = await confirm({ title: '删除？', message: '此操作不可撤销' })
 *   if (ok) doDelete()
 */

import { create } from 'zustand';

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions;
  resolve?: (value: boolean) => void;

  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  close: (result: boolean) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  options: { title: '' },

  confirm: (opts) => {
    return new Promise<boolean>((resolve) => {
      set({ open: true, options: opts, resolve });
    });
  },

  close: (result) => {
    const { resolve } = get();
    resolve?.(result);
    set({ open: false, resolve: undefined });
  },
}));

/** 确认对话框 UI 组件 */
export function ConfirmDialog() {
  const { open, options, close } = useConfirmStore();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-charcoal/30 backdrop-blur-sm" onClick={() => close(false)} />
      <div className="relative w-full max-w-sm bg-white dark:bg-warm-dark rounded-xl shadow-lg p-5">
        <h3 className="text-base font-medium text-charcoal dark:text-charcoal-light">
          {options.title}
        </h3>
        {options.message && (
          <p className="text-sm text-charcoal/60 dark:text-charcoal-light/60 mt-2">
            {options.message}
          </p>
        )}
        <div className="flex gap-2 justify-end mt-4">
          <button
            onClick={() => close(false)}
            className="px-4 py-1.5 rounded-md text-sm text-charcoal/60 dark:text-charcoal-light/60 hover:bg-warm-white dark:hover:bg-border-warm-dark"
          >
            {options.cancelText ?? '取消'}
          </button>
          <button
            onClick={() => close(true)}
            className={`px-4 py-1.5 rounded-md text-sm text-white ${
              options.danger ? 'bg-error hover:opacity-90' : 'bg-camel hover:bg-camel-light'
            }`}
          >
            {options.confirmText ?? '确认'}
          </button>
        </div>
      </div>
    </div>
  );
}
